import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Moon, Sun, Menu, X, BookOpen, ChevronRight, ChevronDown, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DOCS_CATEGORIES, DocsCategory } from "@/data/docs/docsCategories";
import { DOCS_FUNCTIONALITIES, getFunctionalityById } from "@/data/docs/docsFunctionalities";
import { DOCS_GUIDES, Guide } from "@/data/docs/docsGuides";
import { DOCS_MOCKUPS } from "@/components/docs/DocsMockups";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

const Documentation = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [docsTheme, setDocsTheme] = useState<"light" | "dark">(() => {
    return (localStorage.getItem("docs-theme") as "light" | "dark") || "light";
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>(["getting-started"]);

  const activeCategory = searchParams.get("cat") || "getting-started";
  const activeSubcategory = searchParams.get("sub") || "welcome";

  useEffect(() => {
    localStorage.setItem("docs-theme", docsTheme);
  }, [docsTheme]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const navigateTo = (cat: string, sub: string) => {
    setSearchParams({ cat, sub });
    setMobileMenuOpen(false);
  };

  const filteredContent = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const query = searchQuery.toLowerCase();
    
    const matchedFunctionalities = DOCS_FUNCTIONALITIES.filter(f =>
      f.title.toLowerCase().includes(query) ||
      f.description.toLowerCase().includes(query)
    );

    const matchedGuides = DOCS_GUIDES.filter(g =>
      g.title.toLowerCase().includes(query) ||
      g.description.toLowerCase().includes(query) ||
      g.steps.some(s => s.title.toLowerCase().includes(query))
    );

    return { matchedFunctionalities, matchedGuides };
  }, [searchQuery]);

  const currentCategory = DOCS_CATEGORIES.find(c => c.id === activeCategory);

  const renderContent = () => {
    // Search results
    if (filteredContent) {
      return (
        <div className="space-y-8">
          <h2 className="text-2xl font-bold">Resultados para "{searchQuery}"</h2>
          
          {filteredContent.matchedFunctionalities.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Funcionalidades</h3>
              <div className="grid gap-4 md:grid-cols-2">
                {filteredContent.matchedFunctionalities.map(func => (
                  <Card key={func.id} className="cursor-pointer hover:border-primary transition-colors" onClick={() => navigateTo(func.category, func.id)}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2">
                        <func.icon className="h-5 w-5 text-primary" />
                        <CardTitle className="text-base">{func.title}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{func.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {filteredContent.matchedGuides.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Guias</h3>
              <div className="grid gap-4">
                {filteredContent.matchedGuides.map(guide => (
                  <Card key={guide.id} className="cursor-pointer hover:border-primary transition-colors" onClick={() => navigateTo("getting-started", guide.id)}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2">
                        <guide.icon className="h-5 w-5 text-primary" />
                        <CardTitle className="text-base">{guide.title}</CardTitle>
                        <Badge variant="outline">{guide.difficulty}</Badge>
                        <Badge variant="secondary">{guide.duration}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{guide.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {filteredContent.matchedFunctionalities.length === 0 && 
           filteredContent.matchedGuides.length === 0 && (
            <p className="text-muted-foreground">Nenhum resultado encontrado.</p>
          )}
        </div>
      );
    }

    // Welcome page
    if (activeCategory === "getting-started" && activeSubcategory === "welcome") {
      return (
        <div className="space-y-8">
          <div className="text-center py-8">
            <BookOpen className="h-16 w-16 mx-auto text-primary mb-4" />
            <h1 className="text-4xl font-bold mb-4">Bem-vindo à Documentação Justiçai</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Aprenda a usar todas as funcionalidades da plataforma com guias práticos e passo a passo didáticos.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {DOCS_CATEGORIES.map(cat => (
              <Card key={cat.id} className="cursor-pointer hover:border-primary transition-colors" onClick={() => navigateTo(cat.id, cat.subcategories[0].id)}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <cat.icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle>{cat.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{cat.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div>
            <h2 className="text-2xl font-bold mb-4">Guias Populares</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {DOCS_GUIDES.slice(0, 4).map(guide => (
                <Card key={guide.id} className="cursor-pointer hover:border-primary transition-colors" onClick={() => navigateTo("getting-started", guide.id)}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <guide.icon className="h-5 w-5 text-primary" />
                        <CardTitle className="text-base">{guide.title}</CardTitle>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant="outline">{guide.difficulty}</Badge>
                        <Badge variant="secondary">{guide.duration}</Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{guide.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Functionality pages
    const functionality = getFunctionalityById(activeSubcategory);
    if (functionality) {
      return (
        <div className="space-y-8">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-primary/10">
              <functionality.icon className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">{functionality.title}</h1>
            </div>
          </div>
          <p className="text-lg text-muted-foreground">{functionality.description}</p>

          {/* Visual Mockup */}
          {functionality.mockupId && DOCS_MOCKUPS[functionality.mockupId] && (
            DOCS_MOCKUPS[functionality.mockupId]
          )}
          {/* Steps */}
          {functionality.steps && functionality.steps.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Como usar</h2>
              {functionality.steps.map(step => (
                <div key={step.order} className="flex gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <step.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold">{step.order}. {step.title}</h4>
                    <p className="text-muted-foreground mt-1">{step.instruction}</p>
                    {step.tip && (
                      <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 rounded">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200">
                          💡 Dica: {step.tip}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tips */}
          {functionality.tips && functionality.tips.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
              <h3 className="font-semibold mb-2 text-blue-800 dark:text-blue-200">💡 Dicas importantes</h3>
              <ul className="space-y-1 text-sm text-blue-700 dark:text-blue-300">
                {functionality.tips.map((tip, i) => (
                  <li key={i}>• {tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }

    // Guide pages
    const guide = DOCS_GUIDES.find(g => g.id === activeSubcategory);
    if (guide) {
      return (
        <div className="space-y-8">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <guide.icon className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">{guide.title}</h1>
                <div className="flex gap-2 mt-2">
                  <Badge variant="outline">{guide.difficulty}</Badge>
                  <Badge variant="secondary">{guide.duration}</Badge>
                </div>
              </div>
            </div>
            <p className="text-lg text-muted-foreground">{guide.description}</p>
          </div>

          <div className="space-y-4">
            {guide.steps.map(step => (
              <Card key={step.order}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                      {step.order}
                    </span>
                    <div className="flex items-center gap-2">
                      <step.icon className="h-5 w-5 text-muted-foreground" />
                      <CardTitle className="text-lg">{step.title}</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="ml-11">
                  <p>{step.description}</p>
                  {step.tip && (
                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="text-sm">💡 <strong>Dica:</strong> {step.tip}</p>
                    </div>
                  )}
                  {step.warning && (
                    <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-950/30 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <p className="text-sm">⚠️ <strong>Atenção:</strong> {step.warning}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      );
    }

    // Default category content
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">{currentCategory?.title}</h1>
        <p className="text-lg text-muted-foreground">{currentCategory?.description}</p>
        
        <div className="grid gap-4 md:grid-cols-2">
          {currentCategory?.subcategories.map(sub => (
            <Card key={sub.id} className="cursor-pointer hover:border-primary transition-colors" onClick={() => navigateTo(activeCategory, sub.id)}>
              <CardHeader>
                <CardTitle className="text-lg">{sub.title}</CardTitle>
                <CardDescription>{sub.description}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={cn("min-h-screen", docsTheme === "dark" ? "dark bg-background text-foreground" : "bg-white text-gray-900")}>
      {/* Header */}
      <header className={cn("sticky top-0 z-50 border-b", docsTheme === "dark" ? "bg-background border-border" : "bg-white border-gray-200")}>
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="md:hidden">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden">
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <div className="flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-primary" />
              <span className="font-bold text-xl">Documentação Justiçai</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="relative hidden md:block w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar na documentação..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDocsTheme(docsTheme === "light" ? "dark" : "light")}
            >
              {docsTheme === "light" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>
            <Button variant="outline" onClick={() => navigate("/")}>
              Voltar ao Justiçai
            </Button>
          </div>
        </div>
        
        {/* Mobile search */}
        <div className="px-4 pb-3 md:hidden">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={cn(
          "fixed md:sticky top-[57px] md:top-[65px] h-[calc(100vh-57px)] md:h-[calc(100vh-65px)] w-64 border-r overflow-hidden transition-transform z-40",
          docsTheme === "dark" ? "bg-background border-border" : "bg-gray-50 border-gray-200",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}>
          <ScrollArea className="h-full py-4">
            <nav className="px-3 space-y-1">
              {DOCS_CATEGORIES.map(category => (
                <Collapsible
                  key={category.id}
                  open={expandedCategories.includes(category.id)}
                  onOpenChange={() => toggleCategory(category.id)}
                >
                  <CollapsibleTrigger className={cn(
                    "flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    activeCategory === category.id
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted"
                  )}>
                    <div className="flex items-center gap-2">
                      <category.icon className="h-4 w-4" />
                      <span>{category.title}</span>
                    </div>
                    {expandedCategories.includes(category.id) ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </CollapsibleTrigger>
                  <CollapsibleContent className="ml-4 mt-1 space-y-1">
                    {category.subcategories.map(sub => (
                      <button
                        key={sub.id}
                        onClick={() => navigateTo(category.id, sub.id)}
                        className={cn(
                          "flex items-center w-full px-3 py-1.5 rounded-lg text-sm transition-colors",
                          activeSubcategory === sub.id
                            ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 font-medium border-l-4 border-yellow-400"
                            : "hover:bg-muted text-muted-foreground hover:text-foreground"
                        )}
                      >
                        {sub.title}
                      </button>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </nav>
          </ScrollArea>
        </aside>

        {/* Mobile overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <div className="max-w-4xl mx-auto px-6 py-8">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Documentation;
